import adminuserService from '../../../services/adminuserService';
import filterService from '../../../services/filterService';

import {
    setAdminuserLoading,
    setAdminuserError,
    setAdminuserList,
    setAdminuserCurrent,
    setAdminuserPagination,
    setAdminuserSort,
    setAdminuserFilters,
    setAdminuserDefaultFilter,
    setAdminuserSettings,
    clearAdminuserCurrent,
    clearAdminuserError,
    setAdminuserFilterLoading,
    changeAdminuserFilterCondition,
    addAdminuserFilterCondition,
    updateAdminuserFilterConditionRule,
    updateAdminuserFilterConditionValue,
    removeAdminuserFilterCondition,
    setAdminuserSelected,
    setAdminuserDownloading,
    resetAdminuserFiltersAndPagination,

    setAdminuserClassicFilters
} from './adminuserSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().adminuser.isSettingsInitialized) {
            dispatch(setAdminuserLoading(true));
            const response = await adminuserService.getSettings();

            // Set the settings
            dispatch(setAdminuserSettings(response));

            const classicFilters = response?.classic_filters || [];
            dispatch(setAdminuserClassicFilters(classicFilters));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setAdminuserDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setAdminuserError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setAdminuserLoading(false));
    }
};

export const fetchAdminusers = (params = {}, filter = {}, shouldUseFilter, externalCondition = {}) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().adminuser;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : externalCondition;

        dispatch(setAdminuserLoading(true));
        const response = await adminuserService.getList(finalQueryParams, finalActiveFilter, state.classicFilters);
        dispatch(setAdminuserList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchAdminusers:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setAdminuserError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setAdminuserLoading(false));
    }
};

export const fetchAdminuser = (id) => async (dispatch) => {
    try {
        dispatch(setAdminuserLoading(true));
        const response = await adminuserService.getOne(id);
        dispatch(setAdminuserCurrent(response.data));
    } catch (error) {
        dispatch(setAdminuserError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setAdminuserLoading(false));
    }
};

export const createAdminuser = (data) => async (dispatch) => {
    try {
        const response = await adminuserService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateAdminuser = (id, data) => async (dispatch) => {
    try {
        const response = await adminuserService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteAdminuser = (id) => async (dispatch) => {
    try {
        dispatch(setAdminuserLoading(true));
        const response = await adminuserService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setAdminuserError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setAdminuserLoading(false));
    }
};

export const downloadAdminuser = (type) => async (dispatch, getState) => {
    try {
        const state = getState().adminuser;

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            export_type: type
        };

        const filter = state.defaultFilter;
        dispatch(setAdminuserDownloading(true));
        const response = await adminuserService.download(finalQueryParams, filter, state.classicFilters);
        dispatch(setAdminuserDownloading(false));

        return response;
    } catch (error) {
        console.error('Error downloading adminusers:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setAdminuserError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setAdminuserDownloading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setAdminuserPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setAdminuserSort(sort));
};

export const searchAdminusers = (query) => (dispatch, getState) => {
    const currentFilters = getState().adminuser.filters;
    dispatch(setAdminuserFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setAdminuserDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeAdminuserFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addAdminuserFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateAdminuserFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateAdminuserFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeAdminuserFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setAdminuserFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().adminuser.settings;
            dispatch(setAdminuserSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setAdminuserFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setAdminuserFilterLoading(false));
        dispatch(setAdminuserError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setAdminuserSelected(selectedRows));
    };
};

export const clearCurrentAdminuser = () => {
    return (dispatch) => {
        dispatch(clearAdminuserCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetAdminuserFiltersAndPagination());
};

export const changeClassicFilter = (name, value) => (dispatch, getState) => {
    const currentClassicFilters = getState().adminuser.classicFilters;

    // Create a deep copy of the classic filters
    const updatedClassicFilters = { ...currentClassicFilters };
    
    // Update the nested value in the query object
    if (updatedClassicFilters[name]) {
        updatedClassicFilters[name] = {
            ...updatedClassicFilters[name],
            query: {
                ...updatedClassicFilters[name].query,
                ...(typeof value === 'object' && value !== null ? value : { value })
            }
        };
    }

    dispatch(setAdminuserClassicFilters(updatedClassicFilters));
};